import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { languageOptions } from "../../configs/languages";
import { localizePath, stripLocalePrefix } from "../../utils/localePaths";
import { isNonLocalizedPath } from "../../configs/nonLocalizedRoutes";

/**
 * Language selector component with country flags
 * @param {Object} props Component props
 * @param {String} props.currentLanguage Current language code
 * @param {Function} props.onLanguageChange Callback when language is changed
 * @returns {JSX.Element} The language selector component
 */
export const LanguageSelector = ({ onLanguageChange }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOption, setSelectedOption] = useState(null);

  const getCurrentLanguage = () => {
    const i18nLanguage = localStorage.getItem("i18nextLng");
    const account = JSON.parse(localStorage.getItem("accountData"));

    // If the account has a language preference, use it
    if (account?.preferences?.language) {
      return account.preferences.language;
    }

    return i18nLanguage;
  };

  // Initialize the selected option based on the current language
  useEffect(() => {
    const currentLanguage = getCurrentLanguage();
    const currentOption = languageOptions.find(
      (option) => option.value === (currentLanguage || i18n.language)
    );
    setSelectedOption(currentOption || languageOptions[0]);
  }, [i18n.language]);

  const handleLanguageChange = (selectedOption) => {
    setSelectedOption(selectedOption);

    // Change language in i18n
    i18n.changeLanguage(selectedOption.value);

    // Navigate to the locale-prefixed URL for the new language, preserving
    // the current page and query string (minus any stale ?lang= param, now
    // superseded by the path prefix as the source of truth).
    const { basePath } = stripLocalePrefix(location.pathname);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("lang");
    const search = searchParams.toString();
    // English-only pages have no localized URL — keep the visitor on the
    // unprefixed path (content stays English) rather than routing them to a
    // /es/… URL that doesn't exist as its own page.
    const target = isNonLocalizedPath(basePath)
      ? basePath
      : localizePath(basePath, selectedOption.value);
    navigate(`${target}${search ? `?${search}` : ""}`);

    // Call the parent's callback if provided
    if (onLanguageChange) {
      onLanguageChange(selectedOption.value);
    }
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "var(--bs-body-bg)",
      border: "var(--bs-border-width) solid var(--bs-border-color)",
      boxShadow: "none",
      "&:hover": {
        border: "var(--bs-border-width) solid var(--bs-border-hover)",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor:
        state.isSelected || state.isFocused
          ? "var(--bs-light)"
          : "transparent",
      color: "var(--bs-body-color)",
      "&:hover": {
        backgroundColor: "var(--bs-light)",
      },
      cursor: "pointer",
    }),
    menu: (provided) => ({
      ...provided,
      top: "auto",
      bottom: "100%",
      marginBottom: "4px",
      backgroundColor: "var(--bs-body-bg)",
      border: "var(--bs-border-width) solid var(--bs-border-color)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--bs-body-color)",
    }),
    input: (provided) => ({
      ...provided,
      color: "var(--bs-body-color)",
    }),
  };

  const FlagImage = ({ flag, label }) => (
    <img
      src={`/images/flags/${flag}`}
      alt={`${label} flag`}
      width="20"
      height="15"
      className="me-2 rounded-1"
      style={{ objectFit: "cover" }}
      loading="lazy"
      decoding="async"
      fetchpriority="low"
      referrerPolicy="no-referrer"
    />
  );

  // Custom Option component with flag
  const CustomOption = ({ option }) => (
    <div className="d-flex align-items-center">
      <FlagImage flag={option.flag} label={option.label} />
      <span>{option.label}</span>
    </div>
  );

  return (
    <Select
      value={selectedOption}
      onChange={handleLanguageChange}
      options={languageOptions}
      styles={customStyles}
      formatOptionLabel={(option) => CustomOption({ option })}
      classNamePrefix="language-select react-select"
      className="rounded-4"
      inputId="language-select"
      aria-label="Select language"
    />
  );
};
